import type { NormalizedLandmark, NormalizedPoint } from '../types/geometry.js';
import { rectFromPoints } from '../types/geometry.js';

/**
 * Landmark indices for the MediaPipe Face Landmarker (canonical 468/478 mesh).
 *
 * The mesh is mirrored with respect to the viewer: `RIGHT_EYE_*` below refers to
 * the eye that appears on the **left side of the image** — which is what document
 * rules and the overlay actually care about. Every consumer in this codebase
 * re-derives left/right from the pixel coordinates anyway (see `eyeCenters`),
 * so a mirrored model cannot silently swap the two eyes.
 */
export const LANDMARKS = {
  foreheadTopCenter: 10,
  chinBottom: 152,
  nasion: 168,
  noseTip: 4,
  noseBridgeBottom: 2,
  upperLipInner: 13,
  lowerLipInner: 14,
  leftCheekEdge: 234,
  rightCheekEdge: 454,
  leftTemple: 127,
  rightTemple: 356,
  glabellaLeft: 107,
  glabellaRight: 336,
} as const;

/** Six-point eye contour of the eye on the viewer's left. */
export const EYE_A_CONTOUR = [33, 160, 158, 133, 153, 144] as const;
/** Six-point eye contour of the eye on the viewer's right. */
export const EYE_B_CONTOUR = [362, 385, 387, 263, 373, 380] as const;

/** Vertical eyelid pairs (upper, lower) used for the eye-aspect-ratio. */
export const EYE_A_VERTICAL = [159, 145] as const;
export const EYE_B_VERTICAL = [386, 374] as const;

/** Iris centres only exist in the 478-point refinement model. */
export const EYE_A_IRIS_CENTER = 468;
export const EYE_B_IRIS_CENTER = 473;

export const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
] as const;

export const MIN_LANDMARK_COUNT = 468;
export const IRIS_LANDMARK_COUNT = 478;

/** Returns the landmark or `null` when the index is absent. */
export const pick = (
  landmarks: readonly NormalizedLandmark[],
  index: number,
): NormalizedPoint | null => {
  const point = landmarks[index];
  if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null;
  return { x: point.x, y: point.y };
};

/** Mean of several landmark indices, skipping any that are missing. */
export const centroid = (
  landmarks: readonly NormalizedLandmark[],
  indices: readonly number[],
): NormalizedPoint | null => {
  let x = 0;
  let y = 0;
  let count = 0;
  for (const index of indices) {
    const point = pick(landmarks, index);
    if (!point) continue;
    x += point.x;
    y += point.y;
    count += 1;
  }
  if (count === 0) return null;
  return { x: x / count, y: y / count };
};

export const hasIris = (landmarks: readonly NormalizedLandmark[]): boolean =>
  landmarks.length >= IRIS_LANDMARK_COUNT && pick(landmarks, EYE_A_IRIS_CENTER) !== null;

/**
 * Eye centres in *image* coordinates. `eyeA`/`eyeB` are the raw clusters; the
 * caller decides which one is "left" by comparing x, so mirroring in the model
 * cannot swap them.
 */
export const eyeCenters = (
  landmarks: readonly NormalizedLandmark[],
): {
  eyeACenter: NormalizedPoint;
  eyeBCenter: NormalizedPoint;
  source: 'iris' | 'contour';
} | null => {
  if (hasIris(landmarks)) {
    const a = pick(landmarks, EYE_A_IRIS_CENTER);
    const b = pick(landmarks, EYE_B_IRIS_CENTER);
    if (a && b) return { eyeACenter: a, eyeBCenter: b, source: 'iris' };
  }
  const a = centroid(landmarks, EYE_A_CONTOUR);
  const b = centroid(landmarks, EYE_B_CONTOUR);
  if (!a || !b) return null;
  return { eyeACenter: a, eyeBCenter: b, source: 'contour' };
};

/** Bounding box of a landmark subset. */
export const subsetBox = (
  landmarks: readonly NormalizedLandmark[],
  indices: readonly number[],
): { x: number; y: number; width: number; height: number } | null => {
  const points: NormalizedPoint[] = [];
  for (const index of indices) {
    const point = pick(landmarks, index);
    if (point) points.push(point);
  }
  if (points.length === 0) return null;
  return rectFromPoints(points);
};

/** Bounding box of the whole mesh. */
export const meshBox = (
  landmarks: readonly NormalizedLandmark[],
): { x: number; y: number; width: number; height: number } | null => {
  const points: NormalizedPoint[] = [];
  for (const landmark of landmarks) {
    if (Number.isFinite(landmark.x) && Number.isFinite(landmark.y)) {
      points.push({ x: landmark.x, y: landmark.y });
    }
  }
  if (points.length === 0) return null;
  return rectFromPoints(points);
};

/**
 * Eye-aspect-ratio of one eye: vertical eyelid distance over horizontal corner
 * distance. Roughly 0.25–0.35 for an open adult eye, below ~0.15 for a blink.
 * Returns `null` when the required landmarks are missing.
 */
export const eyeAspectRatio = (
  landmarks: readonly NormalizedLandmark[],
  horizontal: readonly [number, number],
  vertical: readonly [number, number],
): number | null => {
  const a = pick(landmarks, horizontal[0]);
  const b = pick(landmarks, horizontal[1]);
  const upper = pick(landmarks, vertical[0]);
  const lower = pick(landmarks, vertical[1]);
  if (!a || !b || !upper || !lower) return null;
  const width = Math.hypot(a.x - b.x, a.y - b.y);
  if (width <= 1e-6) return null;
  const height = Math.hypot(upper.x - lower.x, upper.y - lower.y);
  return height / width;
};
